import type { MiningHullTier } from "./miningMissions";
import { RESOURCE_TIER, volumePerUnitM3 } from "./resourceCatalog";
import type { FactionName } from "./factionStanding";

// Accepted-mission model (design doc 16.34, revised: event-driven
// completion, not progress-tracking). Per-mission stored state is
// deliberately minimal: type, target system(s)/completion criteria, and
// the reward cached at acceptance (so a later rebalance never
// retroactively changes an already-accepted mission's payout).
// `acceptedAt` exists ONLY on VIP -- it's the sole mission type where
// elapsed time itself matters, for its independent deadline check.

// `posterFaction` (16.48/16.55) -- whichever real faction/Pirates
// controls the System this mission's board was generated for, null for a
// board with no faction poster (safe/neutral space). Drives both the
// standing gain on completion and the -1.0 penalty on a player-initiated
// Abandon; a covert-ops mission (not built yet, 16.14/16.55) would always
// carry null here regardless of its board's System, since covert-ops
// missions never touch standing at all.
export interface AcceptedCombatMission {
  id: string;
  type: "Combat";
  encounterId: string;
  systemId: string;
  rewardCr: number;
  posterFaction: FactionName | null;
}

export interface AcceptedMiningMission {
  id: string;
  type: "Mining";
  systemId: string;
  resource: string;
  quotaM3: number;
  // Mission-tagged cargo -- IS the progress, not a separate counter
  // (16.34). Ephemeral in both directions: removed from the hold whether
  // the mission completes (paid) or is abandoned (not paid) -- never
  // converts to general/sellable cargo either way.
  taggedM3: number;
  hullTier: MiningHullTier;
  rewardCr: number;
  posterFaction: FactionName | null;
}

export interface AcceptedHaulingMission {
  id: string;
  type: "Hauling";
  destinationSystemId: string;
  rewardCr: number;
  // Which hull tier's sealed container this is (16.39) -- sizes the real
  // cargo footprint the contract reserves. Optional so saves written before
  // containers had a volume still load; those fall back to reserving
  // nothing, exactly as they did when accepted.
  hullTier?: string;
  posterFaction: FactionName | null;
  // Route travel time (16.28's hop-time formula, src/shipSpeed.ts) this
  // mission's own par-time is measured against (16.56) -- captured at
  // acceptance so a later rebalance never retroactively changes an
  // already-accepted mission's time bonus, same "cache the reward inputs"
  // convention rewardCr already follows.
  parTimeSeconds: number;
  acceptedAt: number;
}

export interface AcceptedVipMission {
  id: string;
  type: "VIP Transport";
  destinationSystemId: string;
  rewardCr: number;
  acceptedAt: number; // epoch ms
  deadlineSeconds: number;
  posterFaction: FactionName | null;
  parTimeSeconds: number; // 16.56 -- same par-time convention as Hauling above
}

export type AcceptedMission =
  | AcceptedCombatMission
  | AcceptedMiningMission
  | AcceptedHaulingMission
  | AcceptedVipMission;

export function createMissionId(): string {
  return crypto.randomUUID();
}

// --- Cargo ledger ----------------------------------------------------
//
// Two independent capacity allowances (16.39, EVE-derived): raw mined
// resources are charged against the ship's dedicated ORE HOLD, everything
// else (wreck loot, trade goods, sealed mission containers) against its
// GENERAL cargo hold. Both live in the same `state.cargo` map -- there's no
// second inventory to shuffle items between, which is the simplification
// over EVE's physically separate holds -- so which allowance an item draws
// from is decided purely by whether it's one of the real mining resources
// (resourceCatalog.ts's RESOURCE_TIER, the same lookup baseStorage.ts
// already uses to route items into Mineral Storage).
//
// `cargo` stores UNIT COUNTS, not m3 (16.40) -- volumePerUnitM3() converts.
// Real ore's per-tier volume (0.1-16 m3/unit) means a stack's stored
// number and the space it occupies are no longer the same number; every
// other item keeps volumePerUnitM3() === 1, so for them nothing changed.

function isOre(itemName: string): boolean {
  return itemName in RESOURCE_TIER;
}

// Everything that is NOT raw ore -- loot, market goods, salvage, artifacts.
// Always volumePerUnitM3 === 1 for these, so summing raw units already IS
// summing m3.
export function generalCargoUsedM3(cargo: Record<string, number>): number {
  // Volume-weighted, like oreUsedM3 below. This used to assume 1 unit =
  // 1 m3 for everything non-ore, which was true while only the 9 real
  // resources had per-unit volume -- 16.74 gave drones real volume too,
  // and that shortcut silently counted a 25 m3 Capital Drone as 1 m3.
  // volumePerUnitM3 still returns 1 for ordinary trade goods, so those
  // are unaffected.
  return Object.entries(cargo).reduce(
    (sum, [name, v]) => (isOre(name) ? sum : sum + v * volumePerUnitM3(name)),
    0
  );
}

// Real ore only, volume-weighted (16.40) -- a stack's unit count times its
// resource's own per-tier volumePerUnitM3, not the raw stored number.
export function oreUsedM3(cargo: Record<string, number>): number {
  return Object.entries(cargo).reduce((sum, [name, v]) => (isOre(name) ? sum + v * volumePerUnitM3(name) : sum), 0);
}

export function taggedCargoUsedM3(missions: AcceptedMission[]): number {
  return missions
    .filter((m): m is AcceptedMiningMission => m.type === "Mining")
    .reduce((sum, m) => sum + m.taggedM3, 0);
}

// Sealed hauling containers (16.26/16.39). The doc locks the container as a
// real, indivisible object ("a sealed, marked container no market will
// buy") but never gave it a volume, so nothing reserved space for it and
// hauler capacity had no effect on how many hauls you could run. Footprint
// is a fixed fraction of the Multi-role hull baseline for that tier (16.6's
// 50/150/400/1120), which lets a dedicated hauler carry roughly four or
// five concurrent contracts.
export const HAULING_CONTAINER_M3: Record<string, number> = { T1: 15, T2: 45, T3: 120, T4: 336 };

export function containerCargoUsedM3(missions: AcceptedMission[]): number {
  return missions
    .filter((m): m is AcceptedHaulingMission => m.type === "Hauling")
    .reduce((sum, m) => sum + (m.hullTier ? (HAULING_CONTAINER_M3[m.hullTier] ?? 0) : 0), 0);
}

// General cargo: non-ore items plus every accepted hauling contract's
// sealed container.
export function freeCargoM3(
  shipCargoM3: number,
  cargo: Record<string, number>,
  missions: AcceptedMission[]
): number {
  return Math.max(0, shipCargoM3 - generalCargoUsedM3(cargo) - containerCargoUsedM3(missions));
}

// Ore hold: raw mined resources plus any mining mission's tagged quota
// cargo (16.34: tagged cargo "occupies real cargo m3" -- it's ore, so it
// draws on the ore hold).
export function freeOreHoldM3(
  shipOreHoldM3: number,
  cargo: Record<string, number>,
  missions: AcceptedMission[]
): number {
  return Math.max(0, shipOreHoldM3 - oreUsedM3(cargo) - taggedCargoUsedM3(missions));
}

// --- Mining: deposit toward a specific mission's tagged cargo ---------

export interface MiningDepositResult {
  taggedM3: number;
  completed: boolean;
}

// Deposits up to `amountM3`, capped by both the mission's remaining quota
// and whatever cargo space is actually free -- if free space runs out
// short of quota, the deposit is capped there and the mission stays
// incomplete (a real capacity constraint, not just a formality). Pure:
// callers apply the result to their own mission list/credits/cargo.
export function depositMining(
  mission: AcceptedMiningMission,
  amountM3: number,
  freeSpaceM3: number
): MiningDepositResult {
  const remainingQuota = mission.quotaM3 - mission.taggedM3;
  const deposit = Math.max(0, Math.min(amountM3, remainingQuota, freeSpaceM3));
  const taggedM3 = mission.taggedM3 + deposit;
  return { taggedM3, completed: taggedM3 >= mission.quotaM3 };
}

// --- Hauling/VIP: arrival check ---------------------------------------

// 16.34: "does the ship hold a matching container" -- in this model, an
// accepted Hauling/VIP mission's container is implicit (held for as long
// as the mission is accepted and not yet delivered/abandoned), so arrival
// alone is the complete check.
export function hasArrivedForDelivery(
  mission: AcceptedHaulingMission | AcceptedVipMission,
  currentSystemId: string
): boolean {
  return mission.destinationSystemId === currentSystemId;
}

// --- VIP: independent deadline check -----------------------------------

export function isVipDeadlineExpired(mission: AcceptedVipMission, nowMs: number): boolean {
  return nowMs > mission.acceptedAt + mission.deadlineSeconds * 1000;
}
